import { useLayoutEffect, useRef, useState, useCallback, useEffect, useMemo, createElement, useDebugValue } from 'react';
import { unstable_runWithPriority, unstable_UserBlockingPriority } from 'scheduler';
import { createContext, useContextUpdate, useContext, useContextSelector, useBridgeValue, BridgeProvider } from 'use-context-selector';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const isSSR = typeof window === 'undefined' || /ServerSideRendering/.test(window.navigator && window.navigator.userAgent);
const useIsoLayoutEffect = isSSR ? fn => fn() : useLayoutEffect;

const MAX_CHAIN_LENGTH = 10;
const SMALL_MAP_SIZE = 10;
const mCreate = () => [new Map()];
const mGet = (m, k) => {
  for (let i = 0; i < m.length; ++i) {
    const v = m[i].get(k);

    if (v) {
      return v;
    }
  }
};

const squash = m => {
  let i = m.length - 1;
  const dst = new Map(m[i]);

  while (i) {
    --i;
    m[i].forEach((v, k) => {
      dst.set(k, v);
    });
  }

  return [dst];
};

const mSet = (m, k, v) => {
  if (m[0].size < SMALL_MAP_SIZE) {
    return [new Map(m[0]).set(k, v), ...m.slice(1)];
  }

  if (m.length >= MAX_CHAIN_LENGTH) {
    m = squash(m);
  }

  return [new Map([[k, v]]), ...m];
};
const mDel = (m, k) => {
  if (m.length > 1) {
    m = squash(m);
  }

  const map = new Map(m[0]);
  map.delete(k);
  return [map];
};
const mKeys = m => {
  const keys = new Set();
  m.forEach(map => {
    for (const key of map.keys()) {
      keys.add(key);
    }
  });
  return keys;
};
const mMerge = (m, newM) => {
  m = [...newM, ...m];

  if (m.length > MAX_CHAIN_LENGTH) {
    m = squash(m);
  }

  return m;
};
const mToPrintable = (m, toPrintableK, toPrintableV) => {
  if (m.length > 1) {
    m = squash(m);
  }

  return new Map([...m[0].entries()].map(([k, v]) => [toPrintableK(k), toPrintableV(v)]));
};

const warningObject = new Proxy({}, {
  get() {
    throw new Error('Please use <Provider>');
  },

  apply() {
    throw new Error('Please use <Provider>');
  }

});

const useWeakMapRef = () => {
  const ref = useRef();

  if (!ref.current) {
    ref.current = new WeakMap();
  }

  return ref.current;
};

const warnAtomStateNotFound = (info, atom) => {
  console.warn('[Bug] Atom state not found. Please file an issue with repro: ' + info, atom);
};

const initialState = mCreate();
const initialUsedState = mCreate();

const updateAtomState = (atom, prevState, partial, prevPromise, isNew) => {
  let atomState = mGet(prevState, atom);

  if (!atomState) {
    if (!isNew && process.env.NODE_ENV !== 'production') {
      warnAtomStateNotFound('updateAtomState', atom);
    }

    atomState = {
      deps: new Set()
    };
  }

  if (prevPromise && prevPromise !== atomState.readP) {
    return prevState;
  }

  return mSet(prevState, atom, _extends({}, atomState, partial));
};

const addDependent = (atom, dependent, prevState) => {
  const atomState = mGet(prevState, atom);

  if (atomState) {
    if (!atomState.deps.has(dependent)) {
      const newDeps = new Set(atomState.deps).add(dependent);
      return mSet(prevState, atom, _extends({}, atomState, {
        deps: newDeps
      }));
    }
  } else if (process.env.NODE_ENV !== 'production') {
    warnAtomStateNotFound('addDependent', atom);
  }

  return prevState;
};

const replaceDependencies = (atom, prevState, dependenciesToReplace) => {
  const dependencies = new Set(dependenciesToReplace);
  let nextState = prevState;
  mKeys(nextState).forEach(a => {
    const aState = mGet(nextState, a);

    if (aState.deps.has(atom)) {
      if (dependencies.has(a)) {
        // not changed
        dependencies.delete(a);
      } else {
        const newDeps = new Set(aState.deps);
        newDeps.delete(atom);
        nextState = mSet(nextState, a, _extends({}, aState, {
          deps: newDeps
        }));
      }
    }
  });
  dependencies.forEach(a => {
    const aState = mGet(nextState, a);

    if (aState) {
      const newDeps = new Set(aState.deps).add(atom);
      nextState = mSet(nextState, a, _extends({}, aState, {
        deps: newDeps
      }));
    } else if (process.env.NODE_ENV !== 'production') {
      warnAtomStateNotFound('replaceDependencies', a);
    }
  });
  return nextState;
};

const readAtomState = (atom, prevState, setState, atomStateCache, force) => {
  if (!force) {
    let atomState = mGet(prevState, atom);

    if (atomState) {
      return [atomState, prevState];
    }

    atomState = atomStateCache.get(atom);

    if (atomState) {
      return [atomState, mSet(prevState, atom, atomState)];
    }
  }

  let isSync = true;
  let nextState = prevState;
  let error = undefined;
  let promise = undefined;
  let value = undefined;
  let dependencies = new Set();
  let flushDependencies = false;

  try {
    const promiseOrValue = atom.read(a => {
      if (dependencies) {
        dependencies.add(a);
      } else {
        setState(prev => addDependent(a, atom, prev));
      }

      if (a !== atom) {
        const [aState, nextNextState] = readAtomState(a, nextState, setState, atomStateCache);

        if (isSync) {
          nextState = nextNextState;
        } else {
          // XXX is this really correct?
          setState(prev => mMerge(nextNextState, prev));
        }

        if (aState.readE) {
          throw aState.readE;
        }

        if (aState.readP) {
          throw aState.readP;
        }

        return aState.value;
      } // a === atom


      const aState = mGet(nextState, a);

      if (aState) {
        if (aState.readP) {
          throw aState.readP;
        }

        return aState.value;
      }

      return a.init; // this should not be undefined
    });

    if (promiseOrValue instanceof Promise) {
      promise = promiseOrValue.then(value => {
        const dependenciesToReplace = dependencies;
        dependencies = null;
        setState(prev => updateAtomState(atom, replaceDependencies(atom, prev, dependenciesToReplace), {
          readE: undefined,
          readP: undefined,
          value
        }, promise));
      }).catch(e => {
        const dependenciesToReplace = dependencies;
        dependencies = null;
        setState(prev => updateAtomState(atom, replaceDependencies(atom, prev, dependenciesToReplace), {
          readE: e instanceof Error ? e : new Error(e),
          readP: undefined
        }, promise));
      });
    } else {
      value = promiseOrValue;
      flushDependencies = true;
    }
  } catch (errorOrPromise) {
    if (errorOrPromise instanceof Promise) {
      promise = errorOrPromise.then(() => {
        setState(prev => readAtomState(atom, mDel(prev, atom), setState, atomStateCache)[1]);
      });
    } else if (errorOrPromise instanceof Error) {
      error = errorOrPromise;
    } else {
      error = new Error(errorOrPromise);
    }

    flushDependencies = true;
  }

  nextState = updateAtomState(atom, nextState, {
    readE: error,
    readP: promise,
    value: promise ? atom.init : value
  }, undefined, true);

  if (flushDependencies) {
    nextState = replaceDependencies(atom, nextState, dependencies);
    dependencies = null;
  }

  const atomState = mGet(nextState, atom);
  isSync = false;
  return [atomState, nextState];
};

const updateDependentsState = (atom, prevState, setState, atomStateCache) => {
  const atomState = mGet(prevState, atom);

  if (!atomState) {
    if (process.env.NODE_ENV !== 'production') {
      warnAtomStateNotFound('updateDependentsState', atom);
    }

    return prevState;
  }

  let nextState = prevState;
  atomState.deps.forEach(dependent => {
    if (dependent === atom || typeof dependent === 'symbol' || !mGet(nextState, dependent)) {
      return;
    }

    const [dependentState, nextNextState] = readAtomState(dependent, nextState, setState, atomStateCache, true);
    const promise = dependentState.readP;

    if (promise) {
      promise.then(() => {
        setState(prev => updateDependentsState(dependent, prev, setState, atomStateCache));
      });
      nextState = nextNextState;
    } else {
      nextState = updateDependentsState(dependent, nextNextState, setState, atomStateCache);
    }
  });
  return nextState;
};

const readAtom = (state, readingAtom, setState, readPendingMap, atomStateCache) => {
  const prevState = readPendingMap.get(state) || state;
  const [atomState, nextState] = readAtomState(readingAtom, prevState, setState, atomStateCache);

  if (nextState !== prevState) {
    readPendingMap.set(state, nextState);
  }

  return atomState;
};

const writeAtom = (writingAtom, update, setState, atomStateCache, addWriteThunk) => {
  const pendingPromises = [];

  const writeAtomState = (prevState, atom, update) => {
    const prevAtomState = mGet(prevState, atom);

    if (prevAtomState && prevAtomState.writeP) {
      const promise = prevAtomState.writeP.then(() => {
        addWriteThunk(prev => writeAtomState(prev, atom, update));
      });
      pendingPromises.push(promise);
      return prevState;
    }

    let nextState = prevState;
    let isSync = true;

    try {
      const promiseOrVoid = atom.write(a => {
        const aState = mGet(nextState, a);

        if (!aState) {
          if (process.env.NODE_ENV !== 'production') {
            warnAtomStateNotFound('writeAtomState', a);
          }

          return a.init;
        }

        if (aState.readP && process.env.NODE_ENV !== 'production') {
          // TODO will try to detect this
          console.warn('Reading pending atom state in write operation. We need to detect this and fallback. Please file an issue with repro.', a);
        }

        return aState.value;
      }, (a, v) => {
        if (a === atom) {
          const partialAtomState = {
            readE: undefined,
            readP: undefined,
            value: v
          };

          if (isSync) {
            nextState = updateDependentsState(a, updateAtomState(a, nextState, partialAtomState), setState, atomStateCache);
          } else {
            setState(prev => updateDependentsState(a, updateAtomState(a, prev, partialAtomState), setState, atomStateCache));
          }
        } else {
          if (isSync) {
            nextState = writeAtomState(nextState, a, v);
          } else {
            addWriteThunk(prev => writeAtomState(prev, a, v));
          }
        }
      }, update);

      if (promiseOrVoid instanceof Promise) {
        pendingPromises.push(promiseOrVoid);
        nextState = updateAtomState(atom, nextState, {
          writeP: promiseOrVoid.then(() => {
            addWriteThunk(prev => updateAtomState(atom, prev, {
              writeP: undefined
            }));
          })
        });
      }
    } catch (e) {
      if (pendingPromises.length) {
        pendingPromises.push(new Promise((_resolve, reject) => {
          reject(e);
        }));
      } else {
        throw e;
      }
    }

    isSync = false;
    return nextState;
  };

  let isSync = true;
  let writeResolve;
  const writePromise = new Promise(resolve => {
    writeResolve = resolve;
  });
  pendingPromises.unshift(writePromise);
  addWriteThunk(prevState => {
    if (isSync) {
      pendingPromises.shift();
    }

    const nextState = writeAtomState(prevState, writingAtom, update);

    if (!isSync) {
      writeResolve();
    }

    return nextState;
  });
  isSync = false;

  if (pendingPromises.length) {
    return new Promise((resolve, reject) => {
      const loop = () => {
        const len = pendingPromises.length;

        if (len === 0) {
          resolve();
        } else {
          Promise.all(pendingPromises).then(() => {
            pendingPromises.splice(0, len);
            loop();
          }).catch(reject);
        }
      };

      loop();
    });
  }
};

const runWriteThunk = (lastStateRef, pendingStateRef, setState, contextUpdate, writeThunkQueue) => {
  while (true) {
    if (!lastStateRef.current || !writeThunkQueue.length) {
      return;
    }

    const thunk = writeThunkQueue.shift();
    const prevState = pendingStateRef.current || lastStateRef.current;
    const nextState = thunk(prevState);

    if (nextState !== prevState) {
      pendingStateRef.current = nextState;
      Promise.resolve().then(() => {
        const pendingState = pendingStateRef.current;

        if (pendingState) {
          pendingStateRef.current = null;
          contextUpdate(() => {
            unstable_runWithPriority(unstable_UserBlockingPriority, () => {
              setState(pendingState);
            });
          });
        }
      });
    }
  }
};

const ActionsContext = createContext(warningObject);
const StateContext = createContext(warningObject);

const InnerProvider = ({
  r,
  children
}) => {
  const contextUpdate = useContextUpdate(StateContext);

  if (!r.current) {
    r.current = contextUpdate;
  }

  return children;
};

const Provider = ({
  children
}) => {
  const contextUpdateRef = useRef();
  const readPendingMap = useWeakMapRef();
  const atomStateCache = useWeakMapRef();
  const [state, setStateOrig] = useState(initialState);
  const lastStateRef = useRef(null);
  const pendingStateRef = useRef(null);
  const setState = useCallback(setStateAction => {
    const pendingState = pendingStateRef.current;

    if (pendingState) {
      pendingStateRef.current = null;
      setStateOrig(pendingState);
    }

    if (lastStateRef.current) {
      const readPending = readPendingMap.get(lastStateRef.current);

      if (readPending) {
        setStateOrig(readPending);

        if (pendingState && process.env.NODE_ENV !== 'production') {
          console.warn('[Bug] conflict pendingState and readPending');
        }
      }
    }

    lastStateRef.current = null;
    setStateOrig(setStateAction);
  }, [readPendingMap]);
  useIsoLayoutEffect(() => {
    const readPending = readPendingMap.get(state);

    if (readPending) {
      setState(readPending);
      return;
    }

    lastStateRef.current = state;
  });
  const [used, setUsed] = useState(initialUsedState);
  useEffect(() => {
    const lastState = lastStateRef.current;
    if (!lastState) return;
    let nextState = lastState;
    let deleted;

    do {
      deleted = false;
      mKeys(nextState).forEach(a => {
        var _mGet;

        const aState = mGet(nextState, a); // do not delete while promises are not resolved

        if (aState.writeP || aState.readP) return;
        const depsSize = aState.deps.size;
        const isEmpty = (depsSize === 0 || depsSize === 1 && aState.deps.has(a)) && !((_mGet = mGet(used, a)) == null ? void 0 : _mGet.size);

        if (isEmpty) {
          atomStateCache.set(a, aState);
          nextState = mDel(nextState, a);
          deleted = true;
        }
      });
    } while (deleted);

    if (nextState !== lastState) {
      setState(nextState);
    }
  }, [used, atomStateCache, setState]);
  const writeThunkQueueRef = useRef([]);
  useEffect(() => {
    runWriteThunk(lastStateRef, pendingStateRef, setState, contextUpdateRef.current, writeThunkQueueRef.current);
  }, [state, setState]);
  const actions = useMemo(() => ({
    add: (id, atom) => {
      setUsed(prev => mSet(prev, atom, new Set(mGet(prev, atom)).add(id)));
    },
    del: (id, atom) => {
      setUsed(prev => {
        const oldSet = mGet(prev, atom);
        if (!oldSet) return prev;
        const newSet = new Set(oldSet);
        newSet.delete(id);

        if (newSet.size) {
          return mSet(prev, atom, newSet);
        }

        return mDel(prev, atom);
      });
    },
    read: (state, atom) => readAtom(state, atom, setState, readPendingMap, atomStateCache),
    write: (atom, update) => writeAtom(atom, update, setState, atomStateCache, thunk => {
      writeThunkQueueRef.current.push(thunk);

      if (lastStateRef.current) {
        runWriteThunk(lastStateRef, pendingStateRef, setState, contextUpdateRef.current, writeThunkQueueRef.current);
      } else {
        // force update (FIXME this is a workaround for now)
        setState(prev => mMerge(prev, mCreate()));
      }
    })
  }), [readPendingMap, atomStateCache, setState]);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useDebugState(state);
  }

  return createElement(ActionsContext.Provider, {
    value: actions
  }, createElement(StateContext.Provider, {
    value: state
  }, createElement(InnerProvider, {
    r: contextUpdateRef
  }, children)));
};

const atomToPrintable = atom => {
  var _atom$debugLabel;

  return `${atom.key}:${(_atom$debugLabel = atom.debugLabel) != null ? _atom$debugLabel : '<no debugLabel>'}`;
};

const stateToPrintable = state => mToPrintable(state, atomToPrintable, v => ({
  value: v.readE || v.readP || v.writeP || v.value,
  deps: Array.from(v.deps).map(atomToPrintable)
}));

const useDebugState = state => {
  useDebugValue(state, stateToPrintable);
};

let keyCount = 0; // global key count for all atoms

function atom(read, write) {
  const config = {
    key: ++keyCount
  };

  if (typeof read === 'function') {
    config.read = read;
  } else {
    config.init = read;

    config.read = get => get(config);

    config.write = (get, set, update) => {
      set(config, typeof update === 'function' ? update(get(config)) : update);
    };
  }

  if (write) {
    config.write = write;
  }

  return config;
}

const isWritable = atom => !!atom.write;

function useAtom(atom) {
  const actions = useContext(ActionsContext);
  const value = useContextSelector(StateContext, useCallback(state => {
    const atomState = actions.read(state, atom);

    if (atomState.readE) {
      throw atomState.readE; // read error
    }

    if (atomState.readP) {
      throw atomState.readP; // read promise
    }

    if (atomState.writeP) {
      throw atomState.writeP; // write promise
    }

    return atomState.value;
  }, [atom, actions]));
  useEffect(() => {
    const id = Symbol();
    actions.add(id, atom);
    return () => {
      actions.del(id, atom);
    };
  }, [actions, atom]);
  const setAtom = useCallback(update => {
    if (isWritable(atom)) {
      return actions.write(atom, update);
    } else {
      throw new Error('not writable atom');
    }
  }, [atom, actions]);
  useDebugValue(value);
  return [value, setAtom];
}

const useBridge = () => {
  const actions = useBridgeValue(ActionsContext);
  const state = useBridgeValue(StateContext);
  return useMemo(() => [actions, state], [actions, state]);
};
const Bridge = ({
  value,
  children
}) => {
  const [actions, state] = value;
  return createElement(BridgeProvider, {
    context: ActionsContext,
    value: actions
  }, createElement(BridgeProvider, {
    context: StateContext,
    value: state
  }, children));
};

export { Bridge, Provider, atom, useAtom, useBridge };
